import React, { HTMLAttributes, memo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { AiAgentWebsiteSource } from '@lgg/isomorphic/types/__generated__/graphql';
import { TableColumns, TableProps } from 'src/components/general/display/table';
import { getDefaultSortOrder, TableSortData } from 'src/components/general/table-helpers';
import { FlexColumn } from 'src/components/layout/flex-column';
import { FlexRow } from 'src/components/layout/flex-row';
import {
  getCreatedAtColumn,
  getDeleteRowOption,
  getRowOptionsColumn,
  KnowledgeSourceStatusTag,
  KnowledgeSourceTable,
  KnowledgeSourceTableFrame,
  TableLightText,
} from 'src/components/pages/ai-agents/components/ai-agents-table-columns';
import { formatBytes } from 'src/components/pages/ai-agents/knowledge-base-sources/websites/helpers';
import { useAuthorization } from 'src/hooks/use-authorization';

const UrlButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.gogo};
  cursor: pointer;
  font-family: ${({ theme }) => theme.font.medium};
  font-size: 13px;
  line-height: 16px;
  padding: 0;
  text-align: left;
`;

const PagesCount = styled.span`
  color: ${({ theme }) => theme.colors.flint};
  font-size: 13px;
  line-height: 16px;
`;

const WebsiteCell = styled(FlexColumn)`
  align-items: flex-start;
  gap: 3px;
`;

const WebsiteLine = styled(FlexRow)`
  align-items: center;
  gap: 5px;
`;

type WebsiteSourcesTableProps = {
  data: AiAgentWebsiteSource[];
  sortData: TableSortData;
  onChange: TableProps<AiAgentWebsiteSource>['onChange'];
  onOpen: (website: AiAgentWebsiteSource) => void;
  onEdit: (website: AiAgentWebsiteSource) => void;
  onDelete: (website: AiAgentWebsiteSource) => void;
};

export const WebsiteSourcesTable = memo<WebsiteSourcesTableProps>(
  ({ data, sortData, onChange, onOpen, onEdit, onDelete }) => {
    const { t } = useTranslation(['aiAgents', 'common']);
    const { hasPermission } = useAuthorization();
    const canUpdate = hasPermission('ai:company.ai_agent_knowledge_base:update:all');
    const canDelete = hasPermission('ai:company.ai_agent_knowledge_base:delete:all');

    const columns: TableColumns<AiAgentWebsiteSource>[] = [
      {
        title: t('aiAgents:knowledge.websites.columnHeaders.website'),
        key: 'title',
        showSorterTooltip: false,
        sorter: true,
        defaultSortOrder: getDefaultSortOrder(
          sortData.key === 'title',
          sortData.direction,
        ),
        render: (_, website) => (
          <WebsiteCell>
            <WebsiteLine>
              <UrlButton
                type="button"
                data-lgg-id="website-sources-cell-url"
                onClick={() => onOpen(website)}
              >
                {website.url}
              </UrlButton>
              {website.mode === 'WEBSITE' && (
                <PagesCount data-lgg-id="website-sources-cell-pages">
                  {t('aiAgents:knowledge.websites.pages', {
                    count: website.pages.totalCount,
                  })}
                </PagesCount>
              )}
            </WebsiteLine>
            <KnowledgeSourceStatusTag
              status={website.status}
              label={t(`aiAgents:knowledge.websites.status.${website.status}`)}
              data-lgg-id="website-status-badge"
            />
          </WebsiteCell>
        ),
      },
      {
        title: t('aiAgents:knowledge.websites.columnHeaders.size'),
        key: 'size',
        width: '97px',
        render: (_, { size }) => (
          <TableLightText data-lgg-id="website-sources-cell-size">
            {formatBytes(size)}
          </TableLightText>
        ),
      },
      getCreatedAtColumn<AiAgentWebsiteSource>({
        title: t('aiAgents:knowledge.websites.columnHeaders.addedTime'),
        sortData,
        testId: 'website-sources-cell-added',
        width: '142px',
      }),
      ...(canUpdate || canDelete
        ? [
            getRowOptionsColumn<AiAgentWebsiteSource>((website) => [
              ...(canUpdate && website.mode === 'WEBSITE'
                ? [
                    {
                      label: t('common:edit'),
                      icon: 'edit',
                      'data-lgg-id': 'website-option-edit',
                      onClick: () => onEdit(website),
                    },
                  ]
                : []),
              ...(canDelete
                ? [
                    getDeleteRowOption({
                      label: t('common:delete'),
                      testId: 'website-option-delete',
                      onClick: () => onDelete(website),
                    }),
                  ]
                : []),
            ]),
          ]
        : []),
    ];

    return (
      <KnowledgeSourceTableFrame>
        <KnowledgeSourceTable
          rowKey={({ id }) => id}
          data-lgg-id="website-sources-table"
          columns={columns}
          tableLayout="fixed"
          loading={false}
          dataSource={data}
          pagination={false}
          showHeader
          scroll={{ x: 700 }}
          onRow={() =>
            ({
              'data-lgg-id': 'website-sources-table-row',
            } as HTMLAttributes<HTMLElement>)
          }
          onChange={onChange}
        />
      </KnowledgeSourceTableFrame>
    );
  },
);
